import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import { supabase } from "../../../lib/supabaseClient";

const DocumentModal = ({ onClose, onAddDocument, artworkId }) => {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState("");
  const [docType, setDocType] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const documentTypes = [
    { id: "certificate", label: t("artworkModals.document.types.certificate") },
    { id: "invoice", label: t("artworkModals.document.types.invoice") },
    { id: "condition_report", label: t("artworkModals.document.types.conditionReport") },
    { id: "exhibition", label: t("artworkModals.document.types.exhibition") },
    { id: "other", label: t("artworkModals.document.types.other") },
  ];

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const maxSize = 30 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        setError(t("artworkModals.document.errors.tooLarge", { name: selectedFile.name }));
        return;
      }
      setFile(selectedFile);
      if (!name && docType) {
        const typeLabel = documentTypes.find(t => t.id === docType)?.label || "";
        setName(`${typeLabel} - ${new Date().toLocaleDateString(i18n.resolvedLanguage || i18n.language)}`);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const selectedFile = e.dataTransfer.files[0];
      const maxSize = 30 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        setError(t("artworkModals.document.errors.tooLarge", { name: selectedFile.name }));
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleTypeChange = (e) => {
    const selectedType = e.target.value;
    setDocType(selectedType);
    if (selectedType && !name) {
      const typeLabel = documentTypes.find(t => t.id === selectedType)?.label || "";
      setName(`${typeLabel} - ${new Date().toLocaleDateString(i18n.resolvedLanguage || i18n.language)}`);
    }
  };


  const safeFileName = (fileName) => {
    const rawName = String(fileName || "document");
    const lastDotIndex = rawName.lastIndexOf(".");
    const rawBase = lastDotIndex > 0 ? rawName.slice(0, lastDotIndex) : rawName;
    const rawExt = lastDotIndex > 0 ? rawName.slice(lastDotIndex + 1) : "pdf";

    const base = rawBase
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9._-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^[-_.]+|[-_.]+$/g, "")
      .toLowerCase();

    const ext = rawExt
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();

    return `${base || "document"}.${ext || "pdf"}`;
  };

  const handleUpload = async () => {
    if (!name.trim()) {
      setError(t("artworkModals.document.errors.nameRequired"));
      return;
    }
    if (!docType) {
      setError(t("artworkModals.document.errors.typeRequired"));
      return;
    }
    if (!file) {
      setError(t("artworkModals.document.errors.fileRequired"));
      return;
    }

    setUploading(true);
    setError(null);

    const cleanFileName = safeFileName(file.name);
    const fileName = `${uuidv4()}-${cleanFileName}`;
    const filePath = artworkId ? `${artworkId}/${fileName}` : fileName;

    try {
      // Récupérer l'userId pour l'owner
      const { data: { user } } = await supabase.auth.getUser();

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
          owner: user?.id,   // ← assigne l'owner explicitement
        });

      if (uploadError) throw uploadError;

      // On stocke le CHEMIN RELATIF, pas l'URL publique
      // L'URL signée sera générée à la demande dans ArtworkDocumentsTab
      // Générer URL signée pour usage immédiat (valide 1h)
      const { data: signedData } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 3600);

      const newDocument = {
        id: uuidv4(),
        name: name,
        storagePath: filePath,   // ex: "artwork-uuid/uuid-fichier.pdf"
        file: signedData?.signedUrl || null,  // URL signée pour affichage
        bucket: 'documents',
        originalName: file.name,
        size: file.size,
        type: docType,
        date: new Date().toISOString()
      };

      onAddDocument(newDocument);
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'upload:", error);
      setError(t("artworkModals.document.errors.upload", { message: error.message }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">{t("artworkModals.document.title")}</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="documentType" className="block text-sm font-medium mb-2">
            {t("artworkModals.document.fields.type")}
          </label>
          <select
            id="documentType"
            value={docType}
            onChange={handleTypeChange}
            className="w-full p-2 border border-gray-300 rounded"
            required
          >
            <option value="">{t("artworkModals.document.fields.selectType")}</option>
            {documentTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="documentName" className="block text-sm font-medium mb-2">
            {t("artworkModals.document.fields.name")}
          </label>
          <input
            type="text"
            id="documentName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t("artworkModals.document.fields.namePlaceholder")}
            required
          />
        </div>

        <div
          className="border-2 border-dashed border-gray-300 p-6 text-center mb-4 rounded"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            className="hidden"
          />
          {file ? (
            <div>
              <p className="font-medium">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-red-600 text-sm mt-2 hover:underline"
              >
                {t("artworkModals.document.actions.remove")}
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-700 mb-4">{t("artworkModals.document.dropzone")}</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                disabled={uploading}
              >
                {t("artworkModals.common.browse")}
              </button>
              <p className="text-xs text-gray-500 mt-2">{t("artworkModals.document.maxSize")}</p>
            </>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
            disabled={uploading}
          >
            {t("artworkModals.common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleUpload}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            disabled={uploading || !file || !name.trim() || !docType}
          >
            {uploading ? t("artworkModals.common.uploading") : t("artworkModals.common.add")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentModal;